import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import { COLORS } from "../constants/colors";

const TermsAndConditions = () => {
  useEffect(() => {
    AOS.init({ duration: 800 });
  }, []);

  const [openSections, setOpenSections] = useState({});

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };
  const sections = [
    {
      title: "1. Acceptance of Terms",
      content:
        "By accessing or using Gurukul ERP, you agree to comply with these terms. If you do not agree, you must not use our services. Please review these terms periodically for any updates.",
    },
    {
      title: "2. User Accounts",
      content:
        "Users (Admin, Students, Teachers) must create an account to access specific features. You are responsible for maintaining your credentials' confidentiality and for all activity under your account.",
    },
    {
      title: "3. Admin Panel",
      content:
        "The Admin Panel provides management tools for users, reports, and operations. Admins must use this panel responsibly. Unauthorized access or misuse is strictly prohibited.",
    },
    {
      title: "4. Student Panel",
      content:
        "Students can access grades, assignments, and resources through their portal. Students must maintain academic integrity. Misuse can lead to disciplinary action.",
    },
    {
      title: "5. Teacher Panel",
      content:
        "Teachers can manage classes, assignments, and communications. They are expected to uphold professionalism and maintain a positive, safe learning environment.",
    },
    {
      title: "6. Library Panel",
      content:
        "Users can borrow, return, and manage library materials. Respect library policies—late returns may incur penalties.",
    },
    {
      title: "7. Limitation of Liability",
      content:
        "Gurukul ERP is not liable for indirect or consequential damages from use of our services, including loss of data or profits. Users assume all usage risks.",
    },
    {
      title: "8. Changes to Terms",
      content:
        "We may modify these terms at any time. Updates become effective immediately upon posting. Continued use signifies acceptance of any modifications.",
    },
    {
      title: "9. Contact Us",
      content:
        "For any questions, please contact us at info@gururkulerp.in. We're here to assist with inquiries or concerns.",
    },
  ];

  return (
    <div
      className="min-h-screen flex flex-col items-center"
      style={{ backgroundColor: COLORS.light }}
    >
      {/* 🌤 Hero Section */}
      <motion.div
        className="w-full py-24 text-center flex flex-col items-center justify-center mb-10"
        style={{
          background: COLORS.gradientHero,
        }}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1
          className="text-5xl font-bold mb-4"
          style={{ color: COLORS.primaryDark }}
        >
          Terms & Conditions
        </h1>
        <p
          className="text-lg font-medium tracking-wide"
          style={{ color: COLORS.textSecondary }}
        >
          Please review the terms carefully before using Gurukul ERP.
        </p>
      </motion.div>

      {/* 💬 Intro Section */}
      <motion.section
        className="max-w-4xl mx-auto text-center px-6 mb-12"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <p className="text-xl leading-relaxed" style={{ color: COLORS.textPrimary }}>
          Welcome to <strong>Gurukul ERP</strong>. By continuing to use our platform, you agree to
          abide by these terms and conditions designed to ensure transparency and trust for all users.
        </p>
      </motion.section>

      {/* 📜 Accordion Terms Section */}
      <motion.div
        className="w-full max-w-5xl bg-white shadow-xl rounded-3xl border border-gray-200 overflow-hidden mb-16"
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7 }}
      >
        {sections.map(({ title, content }, idx) => (
          <div key={idx} className="border-b last:border-none" style={{ borderColor: COLORS.medium }}>
            <button
              onClick={() => toggleSection(title)}
              className="w-full flex justify-between items-center px-6 py-5 text-left transition-colors duration-300"
              style={{
                backgroundColor: openSections[title]
                  ? COLORS.primaryLight
                  : COLORS.white,
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold"
                style={{ color: COLORS.textPrimary }}
              >
                {title}
              </h3>
              <motion.span
                animate={{ rotate: openSections[title] ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="text-2xl"
                style={{ color: COLORS.primaryDark }}
              >
                ▼
              </motion.span>
            </button>

            <AnimatePresence>
              {openSections[title] && (
                <motion.div
                  className="px-6 pb-5"
                  style={{ backgroundColor: COLORS.light }}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <p className="text-base leading-relaxed" style={{ color: COLORS.textSecondary }}>
                    {content}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </motion.div>

      {/* 📞 Footer Section */}
      <motion.footer
        className="text-center mb-20 px-6"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <p className="text-lg" style={{ color: COLORS.textSecondary }}>
          For assistance, please email us at{" "}
          <a
            href="mailto:info@gururkulerp.in"
            className="font-medium underline"
            style={{ color: COLORS.primaryDark }}
          >
            info@gururkulerp.in
          </a>
          .
        </p>
        <p className="text-lg mt-2" style={{ color: COLORS.textSecondary }}>
          Thank you for choosing <strong>Gurukul ERP</strong> as your trusted partner in education.
        </p>
      </motion.footer>
    </div>
  );
};

export default TermsAndConditions;
